import { Loader2, Sparkles, Target } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { type PurposeContextItem, type PurposeRefinement, refinePurpose } from '@/api/services/book-ai-purpose'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useBookAiPurposeStore } from '@/store/useBookAiPurposeStore'
import { useBookAiStore } from '@/store/useBookAiStore'

import { PurposeRefiner } from './purpose-refiner'

const MAX_PURPOSE_CHARS = 300
// どの本にも当てはまる目的の例（押すと入力欄に入るので、本に合わせて書き足せる）
const EXAMPLES = [
  '要点をつかんで人に説明できるようにしたい',
  '仕事や生活で試せることを見つけたい',
  '今の悩みを解決するヒントがほしい',
  '物語の背景やテーマを深く味わいたい',
]
const PLACEHOLDER = '何を知りたいか、何に役立てたいか（具体的に書くほど、効く箇所を絞れます）'

type Props = {
  initial?: string
  /** 保存できたとき */
  onSaved?: () => void
  /** 指定すると「やめる」を出す */
  onCancel?: () => void
  cancelLabel?: string
}

/** AI と磨いている途中の状態（最初に書いた目的・答えた状況・最後の結果） */
type Refining = { original: string; answers: PurposeContextItem[]; result: PurposeRefinement }

/**
 * この本を読む目的の入力欄
 *
 * 書いた目的は「AI と磨く」で、この本をうまく使える形に一緒に磨ける（診断・案・状況を確かめる質問）。
 * 磨いて決めたときは、最初に書いた目的と答えた状況も残す（AI が答え・実践メモ・問いを読者向けにするのに使う）。
 */
export const PurposeEditor = ({ initial = '', onSaved, onCancel, cancelLabel = 'やめる' }: Props) => {
  const save = useBookAiPurposeStore((state) => state.save)
  const isSaving = useBookAiPurposeStore((state) => state.isSaving)
  const current = useBookAiPurposeStore((state) => state.purpose)
  const episodeId = useBookAiPurposeStore((state) => state.episodeId)
  const canRefine = useBookAiStore((state) => !!state.status?.chat_ready)
  const [draft, setDraft] = useState(initial)
  const [refining, setRefining] = useState<Refining | null>(null)
  const [isRefining, setIsRefining] = useState(false)
  const changed = draft.trim() !== initial.trim()

  const decide = async (text: string) => {
    if (!text.trim() || isSaving) return
    const origin = refining ? { original_purpose: refining.original, context: refining.answers } : undefined
    if (await save(text.trim(), origin)) onSaved?.()
  }
  const submit = () => decide(draft)

  const refine = async (newAnswers: PurposeContextItem[] = []) => {
    const text = draft.trim()
    if (!text || episodeId == null || isRefining) return
    // 最初に書いた目的は、前に磨いたときのものを引き継ぐ（無ければ今の下書き）
    const original = refining?.original ?? current?.original_purpose ?? text
    const answers = [...(refining?.answers ?? current?.context ?? []), ...newAnswers]
    setIsRefining(true)
    try {
      const result = await refinePurpose(episodeId, text, answers)
      setRefining({ original, answers, result })
    } catch (error) {
      console.error('目的を磨けませんでした:', error)
      toast.error('AI が目的を磨けませんでした。時間をおいて試してください')
    } finally {
      setIsRefining(false)
    }
  }

  return (
    <div className="space-y-2">
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            submit()
          }
        }}
        placeholder={PLACEHOLDER}
        rows={2}
        maxLength={MAX_PURPOSE_CHARS}
        className="resize-none text-sm"
      />
      {!draft && (
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted"
              onClick={() => setDraft(example)}
            >
              {example}
            </button>
          ))}
        </div>
      )}
      {refining && (
        <PurposeRefiner
          key={refining.answers.length}
          result={refining.result}
          draft={draft.trim()}
          isRefining={isRefining}
          isSaving={isSaving}
          onPick={setDraft}
          onDecide={decide}
          onAnswer={refine}
        />
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
            {cancelLabel}
          </Button>
        )}
        {canRefine && (
          <Button variant="outline" size="sm" onClick={() => refine()} disabled={!draft.trim() || isRefining}>
            {isRefining ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            {refining ? 'この内容で磨き直す' : 'AI と磨く'}
          </Button>
        )}
        <Button size="sm" onClick={submit} disabled={!draft.trim() || (!changed && !refining) || isSaving}>
          {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Target className="size-4" />}
          目的を決める
        </Button>
      </div>
    </div>
  )
}
